/**
 * Automatische Verbindungen (Enable Banking + PayPal): Status, Bankfreigabe,
 * letzter Abruf, Saldo; Aktionen Verbinden / Erneuern / Jetzt synchronisieren / Trennen.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Landmark, RefreshCw, Link2, Unlink, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { financeStatus, financeSync, ebStart, ebDisconnect, consentState, syncSummary } from '@/lib/financeApi';

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');

function StatusLine({ conn }) {
  const consent = consentState(conn.consent_valid_until);
  if (conn.sync_status === 'disconnected') return <span className="text-slate-500">Getrennt</span>;
  if (conn.sync_status === 'expired' || consent.level === 'expired') {
    return <span className="inline-flex items-center gap-1 text-red-700"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />Freigabe abgelaufen — neu verbinden</span>;
  }
  if (conn.sync_error) {
    return <span className="inline-flex items-center gap-1 text-red-700"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />{conn.sync_error}</span>;
  }
  if (consent.level === 'warn') {
    return <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />Freigabe endet in {consent.days} Tagen</span>;
  }
  return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />Aktiv</span>;
}

export default function BankConnectionsPanel({ onSynced }) {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const { data, error, isLoading } = useQuery({
    queryKey: ['finance-status'], queryFn: financeStatus, retry: false, staleTime: 30_000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['finance-status'] });
    onSynced?.();
  };

  const syncMutation = useMutation({
    mutationFn: (connectionId) => financeSync({ connectionId, force: true }),
    onSuccess: (res) => {
      const s = syncSummary(res);
      if (s.errors.length) toast.warning(`Synchronisiert mit Fehlern: ${s.errors.map((e) => e.error).join('; ')}`);
      else toast.success(`Synchronisiert: ${s.text}`);
      refresh();
    },
    onError: (err) => toast.error(err.message),
  });

  const disconnectMutation = useMutation({
    mutationFn: (id) => ebDisconnect(id),
    onSuccess: () => { toast.success('Bankverbindung getrennt. Bisherige Umsätze bleiben erhalten.'); refresh(); },
    onError: (err) => toast.error(err.message),
  });

  const connect = async () => {
    setConnecting(true);
    try {
      const { url } = await ebStart(data?.enableBanking?.aspspName);
      window.location.assign(url);
    } catch (err) {
      toast.error(err.message);
      setConnecting(false);
    }
  };

  const auto = (data?.connections || []).filter((c) => c.external_provider === 'enable_banking' || c.provider === 'paypal');
  const ebReady = data?.enableBanking?.configured;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Landmark className="w-4 h-4 text-slate-500" aria-hidden="true" /> Automatische Verbindungen</span>
          <Button size="sm" variant="outline" className="h-9 gap-1 text-xs" disabled={syncMutation.isPending || !auto.length}
            onClick={() => syncMutation.mutate(undefined)}>
            {syncMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Jetzt synchronisieren
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-xs text-slate-500">Lade Verbindungen …</p>}
        {error && <p className="text-xs text-red-700">{error.message}</p>}

        {auto.map((c) => (
          <div key={c.id} className="flex flex-col gap-2 p-3 rounded-lg border border-slate-200">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {c.display_name || c.aspsp_name || c.provider} <span className="font-normal text-slate-500">· Konto {c.linked_account_no}</span>
              </p>
              <p className="text-xs mt-0.5"><StatusLine conn={c} /></p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Letzter Abruf {fmtDateTime(c.last_sync_at)}
                {c.consent_valid_until && ` · Freigabe bis ${fmtDate(c.consent_valid_until)}`}
              </p>
            </div>
            {c.balance != null && (
              <div>
                <p className="text-sm font-semibold tabular-nums text-slate-900">{fmtEUR(c.balance)}</p>
                <p className="text-[11px] text-slate-500">Saldo {fmtDateTime(c.balance_at)}</p>
              </div>
            )}
            {c.external_provider === 'enable_banking' && (
              <div className="flex gap-2">
                {(!c.verbunden || ['warn', 'expired'].includes(consentState(c.consent_valid_until).level) || c.sync_status === 'expired') && (
                  <Button size="sm" variant="outline" className="h-9 text-xs gap-1" disabled={connecting || !ebReady} onClick={connect}>
                    <Link2 className="w-3.5 h-3.5" /> Erneuern
                  </Button>
                )}
                {c.verbunden && (
                  <Button size="sm" variant="outline" className="h-9 text-xs gap-1 text-red-700" disabled={disconnectMutation.isPending}
                    onClick={() => { if (window.confirm('Bankverbindung trennen? Bisherige Umsätze bleiben erhalten.')) disconnectMutation.mutate(c.id); }}>
                    <Unlink className="w-3.5 h-3.5" /> Trennen
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}

        <div className={cn('flex flex-col gap-2', auto.length && 'pt-1')}>
          <Button className="h-11 gap-2 bg-slate-900 text-white text-xs font-bold" disabled={connecting || !ebReady} onClick={connect}>
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {data?.enableBanking?.aspspName || 'Bankkonto'} verbinden
          </Button>
          <p className="text-[11px] text-slate-500">
            {ebReady
              ? 'Anmeldung direkt bei Ihrer Bank (nur Lesezugriff, Freigabe bis 180 Tage). Umsätze werden täglich abgerufen; eindeutige Zahlungen werden automatisch gebucht.'
              : 'Zuerst Enable Banking unter Admin → Integrationen einrichten.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
